// The items-list inputs of a matrix sub-item block, ported from the shared coffee list view
// (SubItems.Matrix.ItemsListView#calcItemsListProps) for this family alone.
//
// The coffee view still serves matrix, heatmap, rapid-association, menu-test and the correlation
// finder, so only the branches a matrix batch actually reaches are here. What makes this list its
// own is how a row names itself: a scale question draws stars, a smiley scale draws faces, and a
// polarity scale reads its label through the question's scale template.

import { encodeLabel, formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import {
  getCustomVariables,
  getModelValue,
  getReportVirtual,
  getSurveyVariables,
  getTrait,
  getFeature
} from '@aytm/app/stats/lib/stat-page'
import {
  isClickable,
  isRType as calcIsRType,
  isTraitType as calcIsTraitType,
  processText
} from '@aytm/app/stats/page/components/lib/utils'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// the palette repeats every 21 rows, and index 0 is reserved for a trait-coloured row
const COLOR_INDEXES = 21

const colorIndexOf = position => ((position - 1) % COLOR_INDEXES) + 1

// The trait the report is broken down by, wherever it is defined.
const resolveTrait = traitName => {
  if (null == traitName) return null

  return (
    getTrait() ||
    getSurveyVariables('variables')?.[traitName] ||
    getSurveyVariables('pipingVariables')?.[traitName] ||
    getCustomVariables()?.[traitName] ||
    getReportVirtual()[traitName]
  )
}

const traitValuesOf = trait => {
  if (!trait) return []

  return (trait.values || trait.answerVariants || []).map(value => {
    const id =
      (Object.hasOwn(value, 'id') && value.id) || (Object.hasOwn(value, 'position') && `a${value.position}`) || value
    const name =
      (Object.hasOwn(value, 'name') && encodeLabel(value.name.en || value.name)) ||
      (Object.hasOwn(value, 'position') && `A${value.position}`) ||
      value

    return { id, name }
  })
}

const answerHasBase = answer => {
  if (!answer) return false

  if (Object.hasOwn(answer, 'base')) return true

  return Object.keys(answer).some(key => null != answer[key] && Object.hasOwn(answer[key], 'base'))
}

// What the first column is a list of, which the question type names.
const calcTitle = questionType => {
  if ('aro' === questionType) return 'Alternatives (compared items)'
  if ('ro' === questionType) return 'Positions'

  return 'Answers'
}

// What the progress column says it is measuring, which depends on what the batch counts.
const calcProgressTitle = ({ answerType, answer, questionType, hasBase, hideTotal, rType }) => {
  if ('amount' !== answerType) {
    return hideTotal ? '' : `<small>(N=${formatAmounts(answer, 'total', 0)})</small>`
  }

  if (hasBase) return `<small>(based on ${formatAmounts(answer, ['base', 'total'])} responses)</small>`

  if ('aro' === questionType) {
    return hideTotal ? 'Units' : `Units<small>(from ${formatAmounts(answer, 'total')} resp.)</small>`
  }

  if (hideTotal) return ''

  const counted = 'cases' === rType ? 'Respondents' : 'Responses'
  const total = 'cases' === rType ? 'total' : 'respTotal'

  return `${counted}:<small>${formatAmounts(answer, total)}</small>`
}

const calcTraitTable = ({ id, trait, traitName, traitValues, answer, answerVariantPosition }) => ({
  id: `${id}-traits`,
  cols: [
    {
      type: 'title',
      title: encodeLabel(
        (Object.hasOwn(trait, 'title') && trait.title) ||
          (Object.hasOwn(trait, 'id') && traitName.toUpperCase()) ||
          traitName
      )
    },
    // the share names itself where the % marker would sit; the column heads no count slot of its own
    { type: 'progress', title: '', percentLabel: '% of this answer', format: value => formatAmounts(value) }
  ],
  rows: traitValues.map((value, index) => ({
    position: index + 1,
    // the chart draws this value as one segment of the answer's own column, and the two find each
    // other by name -- the row's own position numbers the trait value, not the answer
    pointId: `a${answerVariantPosition}t${index + 1}`,
    cells: [
      { title: value.name.en || value.name, colorIndex: colorIndexOf(index + 1) },
      { amount: answer[value.id]?.amount || 0, percents: answer[value.id]?.percents || 0 }
    ]
  })),
  classNames: ['child']
})

// A scale question ranks its answers, so the row is drawn as that many stars rather than as text.
const calcRankTitle = variant =>
  variant.positive ? Array.from({ length: variant.position }, () => '<i class="icon-aytmfd-star"></i>').join('') : 'N/A'

// A smiley scale names its answers with a face and a word, picked from the set matching the
// question's sub-type and how many answers it offers.
const calcSmileTitle = ({ variant, questionSubType, variantCount }) => {
  const smile = PageGlobals.SMILE_ANSWERS.find(
    item => item.matrix_sub_type === questionSubType && item.values.length === variantCount
  )

  const value = smile?.values?.[variant.position - 1]
  if (!value) return processText(variant.text)

  return `<i class='icon-aytmfd-${value.icon}'></i> - ${value.text}`
}

// How a row names itself, which is the one thing every matrix flavour does differently.
const calcRowTitle = ({ variant, questionType, questionSubType, variantCount, degree, scaleTmpl }) => {
  if ('sr' === questionType) return calcRankTitle(variant)
  if ('se' === questionType) return calcSmileTitle({ variant, questionSubType, variantCount })
  if ('ps' === questionType) return processText(String(scaleTmpl ?? '').replace(degree, variant.text))

  return processText(variant.text)
}

// A polarity-scale row reads through the question's scale template, which already names the answer,
// so it carries no reference label of its own.
const isRefLabelled = questionType => 'ps' !== questionType

// The column the stored sort names, marked with the direction it is sorted in. An order naming a
// column the list does not offer falls to the last one, the way the coffee list resolved it.
const applySortedColumn = (cols, sortOrder) => {
  const parts = String(sortOrder).split('-')

  const found =
    1 < parts.length
      ? cols.findIndex(col => col.param === parts[0])
      : cols.findIndex(col => (('i' === sortOrder[0] && 'title') || 'progress') === col.type)

  const index = 0 > found ? cols.length - 1 : found
  const direction = 2 === parts.length ? ('asc' === parts[1] && 1) || -1 : ('asc' === sortOrder.slice(1) && 1) || -1

  return cols.map((col, i) => (i === index ? { ...col, sorted: direction } : col))
}

export const calcTableProps = ({
  id,
  questionId,
  questionType,
  questionSubType,
  format,
  components,
  answer,
  answerVariants,
  answerType = 'amount',
  degree,
  scaleTmpl,
  sortOrder = 'iasc',
  clickable = true,
  // the list draws its own magnitudes only where the report draws no chart to read them from
  bars = false,
  handlers = {}
} = {}) => {
  const variants = (answerVariants || []).filter(av => av)
  if (!variants.length) return { id, cols: [], rows: [] }

  const rType = calcIsRType({ questionType, questionSubType }) ? getModelValue('rType') : 'cases'

  const traitName = getModelValue('trait')
  const trait = resolveTrait(traitName)
  const traitValues = traitValuesOf(trait)

  const isTraitType = calcIsTraitType({ format, questionType })
  const isLoading = 'loading' === answer
  const hasBase = answerHasBase(answer)

  const hideTotal = true === format?.hideTotal
  const ignoreRefs = true === format?.ignoreRefs
  const ignoreAmount = true === format?.percentsOnly

  const percentDecimals = Object.hasOwn(format || {}, 'percentDecimals')
    ? format.percentDecimals
    : getModelValue('percentDecimals')

  const commentsOn =
    (!components || undefined === components.comments || true === components.comments) &&
    'off' !== getFeature('individual_data')?.status?.oe

  const cols = [
    {
      type: 'title',
      title: calcTitle(questionType),
      sort: (aRow, bRow, dir) => dir * (aRow.position - bRow.position)
    },
    {
      type: 'progress',
      title: calcProgressTitle({ answerType, answer, questionType, hasBase, hideTotal, rType }),
      percentDecimals,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].percents + aRow.position / 10000 - (bRow.cells[1].percents + bRow.position / 10000))
    }
  ]

  const rows = variants.map(variant => {
    const variantAnswer = answer?.[`a${variant.position}`] || {}

    const title = {
      title: calcRowTitle({
        variant,
        questionType,
        questionSubType,
        variantCount: variants.length,
        degree,
        scaleTmpl
      }),
      colorIndex: trait && isTraitType ? 0 : colorIndexOf(variant.position)
    }

    if (!ignoreRefs && isRefLabelled(questionType)) title.label = `A${variant.position}`

    if (Object.hasOwn(variant, 'image')) title.image = variant.image

    if (commentsOn && Object.hasOwn(variantAnswer, 'comments')) {
      title.comments = variantAnswer.comments
      title.onCommentsClick = handlers.onCommentsClick
    }

    if (isClickable(format, clickable)) title.onLabelClick = handlers.onLabelClick

    // counting responses, a row reads its share of the responses, as the question's own chart does; one
    // that carries none reads 0 and no count, as master's list did
    const noResponseShare = 'cases' !== rType && undefined === variantAnswer.respPercents
    const value = {
      percents: ('cases' === rType ? variantAnswer.percents : variantAnswer.respPercents) || 0,
      loading: isLoading
    }
    if (!ignoreAmount) value.amount = noResponseShare ? 0 : variantAnswer[answerType] || 0

    const row = { position: variant.position, cells: [title, value] }

    // a trait breakdown is a table of its own the row expands into
    if (trait && isTraitType && undefined !== variantAnswer[traitName]) {
      row.table = calcTraitTable({
        id,
        trait,
        traitName,
        traitValues,
        answer: variantAnswer[traitName],
        answerVariantPosition: variant.position
      })
    }

    return row
  })

  // Only the answers set the scale: the largest fills the rail and the rest are read against it.
  if (bars) {
    const measured = rows.filter(row => Number.isFinite(row.cells[1]?.percents))
    const largest = Math.max(...measured.map(row => Math.abs(row.cells[1].percents)), 0)

    if (0 < largest) measured.forEach(row => (row.cells[1].share = Math.abs(row.cells[1].percents) / largest))
  }

  const table = { id, questionId, cols: applySortedColumn(cols, sortOrder), rows, onSort: handlers.onSort }

  if (bars) table.classNames = [...(table.classNames || []), 'bars']

  return table
}
